import React, { useEffect, useRef, useState } from "react";
import { X, Briefcase, Heart, Home, Users, ArrowRight } from "lucide-react";
import {
    detectRegisterFromLabel,
    REGISTER_BY_KEY,
    SKIP_RULE_DEFAULT_REGISTER,
} from "@/lib/registers";

/**
 * Chat creation panel — runs the spec's Priority 1→4 register detection
 * inline. Replaces the old "+ New" instant-create flow.
 *
 *   Phase A — "label" : user types a chat label. Hint text below.
 *   Phase B — "register" : ONLY for Priority 3 + 4. User picks one of
 *     four buttons (or dismisses via X → falls back to Peer per spec).
 *
 * Priority 1 + 2 skip Phase B and create the chat immediately with the
 * detected register and register_confirmed=true.
 *
 * Props:
 *   onCreate(name, { register, registerConfirmed, isFirstName }) — called
 *     with the final name + opts. Caller persists the chat record.
 *   onCancel() — close the panel without creating anything.
 */
const REGISTER_BUTTONS = [
    {
        key: "work",
        label: "Work contact",
        icon: Briefcase,
        iconClass: "bg-sky-50 text-sky-700 ring-1 ring-sky-100",
    },
    {
        key: "friend",
        label: "Friend",
        icon: Users,
        iconClass: "bg-violet-50 text-violet-700 ring-1 ring-violet-100",
    },
    {
        key: "family",
        label: "Family",
        icon: Home,
        iconClass: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
    },
    {
        key: "close",
        label: "Close partner",
        icon: Heart,
        iconClass: "bg-rose-50 text-rose-600 ring-1 ring-rose-100",
    },
];

const CreateChatPanel = ({ onCreate, onCancel }) => {
    const [phase, setPhase] = useState("label"); // "label" | "register" | "contacts"
    const [label, setLabel] = useState("");
    const [pendingDetection, setPendingDetection] = useState(null);
    // Carries the register decision through to the contacts phase so the
    // final onCreate() call has everything (register + contacts).
    const [pendingRegister, setPendingRegister] = useState(null);
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (phase === "label") {
            window.requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [phase]);

    // ESC closes the panel from either phase.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onCancel?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onCancel]);

    const finalize = (extra = {}) => {
        if (!pendingRegister) return;
        onCreate(label.trim(), {
            ...pendingRegister,
            phone_number: phone.trim() || null,
            email_address: email.trim() || null,
            ...extra,
        });
    };

    const commitLabel = () => {
        const trimmed = label.trim();
        if (!trimmed) return;
        const detection = detectRegisterFromLabel(trimmed);
        if (detection.confirmed) {
            // Priority 1 or 2 → silent assignment, skip register Q but still
            // show the contacts step so the user can opt-in to direct send.
            setPendingRegister({
                register: detection.register,
                registerConfirmed: true,
                isFirstName: false,
            });
            setPhase("contacts");
            return;
        }
        // Priority 3 or 4 → ask the register question.
        setPendingDetection(detection);
        setPhase("register");
    };

    const handleRegisterPick = (registerKey) => {
        if (!pendingDetection) return;
        setPendingRegister({
            register: registerKey,
            registerConfirmed: true,
            isFirstName: !!pendingDetection.isFirstName,
        });
        setPhase("contacts");
    };

    const handleSkip = () => {
        // Spec skip rule: dismiss without tapping → register = Peer,
        // register_confirmed = true, chat opens, never asked again.
        if (phase === "register" && pendingDetection) {
            setPendingRegister({
                register: SKIP_RULE_DEFAULT_REGISTER,
                registerConfirmed: true,
                isFirstName: !!pendingDetection.isFirstName,
            });
            setPhase("contacts");
            return;
        }
        if (phase === "contacts") {
            // Skip contacts → create without them.
            finalize();
            return;
        }
        onCancel?.();
    };

    return (
        <div
            data-testid="create-chat-panel"
            data-phase={phase}
            className="px-4 sm:px-5 py-4 border-b border-border/60 bg-stone-50/40"
        >
            <div className="flex items-center justify-between mb-2.5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                    New chat
                </p>
                <button
                    type="button"
                    data-testid="btn-cancel-create-chat"
                    onClick={phase === "register" ? handleSkip : onCancel}
                    aria-label="Cancel"
                    className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center justify-center transition-colors"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>

            {phase === "label" && (
                <>
                    <input
                        ref={inputRef}
                        data-testid="input-new-chat-label"
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                commitLabel();
                            }
                        }}
                        maxLength={60}
                        placeholder="e.g. John, Manager, John-Colleague…"
                        className="w-full px-3 py-2 text-[14px] bg-card border border-border rounded-xl outline-none focus:border-[#5E7153] focus:ring-2 focus:ring-[#5E7153]/15 transition"
                    />
                    <p className="mt-1.5 text-[12px] text-muted-foreground leading-relaxed">
                        Add a label to get personalised insights.
                    </p>
                    <div className="mt-3 flex justify-end">
                        <button
                            type="button"
                            data-testid="btn-continue-create-chat"
                            onClick={commitLabel}
                            disabled={!label.trim()}
                            className={[
                                "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors",
                                label.trim()
                                    ? "bg-[#5E7153] text-white hover:bg-[#4B6143]"
                                    : "bg-stone-200 text-muted-foreground cursor-not-allowed",
                            ].join(" ")}
                        >
                            Continue
                            <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </>
            )}

            {phase === "register" && pendingDetection && (
                <div data-testid="register-question">
                    <p className="text-[13.5px] text-foreground leading-snug">
                        Who is{" "}
                        <span className="font-semibold">{label.trim()}</span>{" "}
                        to you?
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        {REGISTER_BUTTONS.map((reg) => {
                            const Icon = reg.icon;
                            return (
                                <button
                                    key={reg.key}
                                    type="button"
                                    data-testid={`register-question-${reg.key}`}
                                    onClick={() => handleRegisterPick(reg.key)}
                                    className="w-full inline-flex items-center gap-2 px-2.5 py-2 rounded-xl border border-border bg-card hover:border-[#5E7153]/40 hover:bg-stone-50 text-[12.5px] text-left transition-colors"
                                >
                                    <span
                                        className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${reg.iconClass}`}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                    </span>
                                    <span className="font-medium text-foreground">
                                        {reg.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-3 text-[12px] text-muted-foreground leading-relaxed">
                        Helps personalise your insights. You can change this
                        anytime.
                    </p>
                </div>
            )}

            {phase === "contacts" && (
                <div data-testid="create-chat-contacts">
                    <p className="text-[13.5px] text-foreground leading-snug">
                        Send messages directly to{" "}
                        <span className="font-semibold">{label.trim()}</span>?
                    </p>
                    <p className="mt-1 text-[12px] text-muted-foreground leading-relaxed">
                        Optional. Stored only in your account, never shared.
                    </p>
                    <div className="mt-3 space-y-3">
                        <div>
                            <label
                                htmlFor="new-chat-phone"
                                className="text-[12px] font-medium text-foreground/80"
                            >
                                WhatsApp number
                            </label>
                            <input
                                id="new-chat-phone"
                                data-testid="input-new-chat-phone"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+91 98765 43210"
                                className="mt-1 w-full px-3 py-2 text-[14px] bg-card border border-border rounded-xl outline-none focus:border-[#5E7153] focus:ring-2 focus:ring-[#5E7153]/15 transition"
                            />
                            <p className="mt-1 text-[11.5px] text-muted-foreground">
                                To send directly via WhatsApp
                            </p>
                        </div>
                        <div>
                            <label
                                htmlFor="new-chat-email"
                                className="text-[12px] font-medium text-foreground/80"
                            >
                                Email address
                            </label>
                            <input
                                id="new-chat-email"
                                data-testid="input-new-chat-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@gmail.com"
                                className="mt-1 w-full px-3 py-2 text-[14px] bg-card border border-border rounded-xl outline-none focus:border-[#5E7153] focus:ring-2 focus:ring-[#5E7153]/15 transition"
                            />
                            <p className="mt-1 text-[11.5px] text-muted-foreground">
                                To send directly via Gmail
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            data-testid="btn-skip-contacts"
                            onClick={handleSkip}
                            className="text-[12.5px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                        >
                            Skip for now
                        </button>
                        <button
                            type="button"
                            data-testid="btn-save-contacts"
                            onClick={() => finalize()}
                            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium bg-[#5E7153] text-white hover:bg-[#4B6143] transition-colors"
                        >
                            Save &amp; continue
                            <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateChatPanel;

// Helper for the rename-flow register row (Step 5): label-only lookup
// keeps the rename UI free of the detection machinery.
export function userFacingRegisterLabel(key) {
    return REGISTER_BY_KEY[key]?.label || "Not set";
}
